import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
	AuthData,
	SHOW_SECOND_PRECISION_ON_ENDORSEMENT_DATES_ENABLED,
} from '../context/auth';
import { PolicyData, withPolicyContext } from '../context/policy';
import { EndorsementResponse } from '../types/endorsements';
import { tableStyles } from '../util/styles';
import { getComparator, HeadCell, Order, stableSort } from '../util/tableUtil';
import { capitalize } from '../util/util';
import { StyledEmptyMessage } from './common/index';
import {
	DATE_PRECISION,
	DATE_PRECISION_INPUT_FORMAT,
} from './fastForm/data/constants';
import { dateConversion } from './fastForm/utils';
import SortableTable from './SortableTable';

interface Props {
	endorsements: EndorsementResponse[];
	authData: AuthData;
	policyData: PolicyData;
}

interface Data {
	endorsementId: number;
	endorsementName: string;
	dateCreated: string;
	effectiveDate: string;
	lastUpdated: string;
	status: string;
	link: string;
}

function createData(
	endorsementId: number,
	endorsementName: string,
	dateCreated: string,
	effectiveDate: string,
	lastUpdated: string,
	status: string,
	link: string,
): Data {
	return {
		endorsementId,
		endorsementName,
		dateCreated,
		effectiveDate,
		lastUpdated,
		status,
		link,
	};
}

const EndorsementsTable = (props: Props) => {
	const { t } = useTranslation();
	const { endorsements, authData, policyData } = props;
	const timezone = authData.auth?.timezone;
	const isSecondPrecisionEnabled =
		authData.auth.configProperties[
			SHOW_SECOND_PRECISION_ON_ENDORSEMENT_DATES_ENABLED
		];
	const DATE_PICKER_PRECISION = isSecondPrecisionEnabled
		? DATE_PRECISION.SECOND
		: DATE_PRECISION.DAY;

	const { productConfig } = policyData;
	const [order, setOrder] = React.useState<Order>('asc');
	const [orderBy, setOrderBy] = React.useState<string>('endorsementId');

	const emptyTable = !endorsements.length ? (
		<StyledEmptyMessage>No {t('endorsement.plural.upper')} </StyledEmptyMessage>
	) : null;
	const classes = tableStyles();

	const endorsementConfigs = productConfig.policyConfiguration.endorsements;

	const headCells: HeadCell[] = [
		{
			id: 'endorsementId',
			rightAlign: false,
			label: `${t('endorsement.singular.upper')} ID`,
		},
		{
			id: 'endorsementName',
			rightAlign: false,
			label: `${t('endorsement.singular.upper')} Name`,
		},
		{ id: 'dateCreated', rightAlign: false, label: 'Date Created' },
		{ id: 'effectiveDate', rightAlign: false, label: 'Effective Date' },
		{ id: 'lastUpdated', rightAlign: false, label: 'Last Updated' },
		{ id: 'status', rightAlign: false, label: 'Current Status' },
	];

	const rows: any = endorsements.map((endorsement: any, index: number) => {
		const defaultUrl =
			endorsement.state === 'application' ? `policyDetails` : `price-breakdown`;
		const endorsementDisplayName = endorsementConfigs.find(
			(e) => e.name === endorsement.endorsementName,
		).displayName;
		return createData(
			endorsement.locator,
			endorsementDisplayName,
			endorsement.createdTimestamp,
			endorsement.startTimestamp.toString(),
			endorsement.updatedTimestamp.toString(),
			capitalize(endorsement.state),
			`${endorsement.locator}/${defaultUrl}`,
		);
	});

	const rowElements = stableSort(rows, getComparator(order, orderBy)).map(
		(row: any, index: number) => {
			const effectiveDate = dateConversion
				.convertTimestampToDayJS(row.effectiveDate, timezone)
				.format(DATE_PRECISION_INPUT_FORMAT[DATE_PICKER_PRECISION]);
			return (
				<TableRow
					component={Link}
					to={row.link}
					hover
					tabIndex={-1}
					key={row.endorsementId}
					className={classes.tableRow}
				>
					<TableCell scope='row'>{row.endorsementId}</TableCell>
					<TableCell>{row.endorsementName}</TableCell>
					<TableCell>{authData.getFormattedDate(row.dateCreated)}</TableCell>
					<TableCell>{effectiveDate}</TableCell>
					<TableCell>{authData.getFormattedDate(row.lastUpdated)}</TableCell>
					<TableCell>{row.status}</TableCell>
				</TableRow>
			);
		},
	);

	return (
		<SortableTable
			rows={rowElements}
			headCells={headCells}
			additionalMsg={emptyTable}
			order={order}
			setOrder={setOrder}
			orderBy={orderBy}
			setOrderBy={setOrderBy}
		/>
	);
};

export default withPolicyContext<Props>(EndorsementsTable);
